import { useEffect, useRef, useState } from 'react';
import { getApiErrorMessage, heldOrdersAPI } from '@matix/api-client';
import { useManagerPrompt } from '../../store/managerPrompt';
import { useLang } from '../../lib/language';

/** The browser can draw a TEXT field as dots (Chrome, Edge, Safari, WebViews). */
const MASKABLE_TEXT =
  typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('-webkit-text-security', 'disc');

/** A field name no password manager maps to a login. */
const FIELD_NAME = 'mx-approval-code';

/**
 * The manager's password — the fixed user whose LoginName is 'Admin'.
 * Opened by `askManagerPassword()`; the password is checked by the server
 * BEFORE the dialog closes, so a caller only ever receives a correct one.
 * Mounted once, in the app shell.
 */
export function ManagerPasswordModal(): JSX.Element | null {
  const { t } = useLang();
  const open = useManagerPrompt((s) => s.open);
  const reason = useManagerPrompt((s) => s.reason);
  const finish = useManagerPrompt((s) => s.finish);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) {
      // Closed: the typed password does not linger in the component.
      setPassword('');
      return undefined;
    }
    setPassword('');
    setError(null);
    setBusy(false);
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish(null);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, finish]);

  if (!open) return null;

  async function submit(): Promise<void> {
    if (!password || busy) return;
    setBusy(true);
    setError(null);
    try {
      await heldOrdersAPI.verifyManager(password);
      finish(password);
    } catch (err) {
      setBusy(false);
      const status = (err as { response?: { status?: number } }).response?.status;
      setError(
        status === 403
          ? t({ en: 'Wrong manager password.', ar: 'كلمة مرور المدير غير صحيحة.' })
          : getApiErrorMessage(err) || t({ en: 'Could not check the password.', ar: 'تعذّر التحقق من كلمة المرور.' }),
      );
      inputRef.current?.select();
    }
  }

  return (
    <div
      className="receipt-view-backdrop"
      style={{ zIndex: 1300 }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) finish(null);
      }}
    >
      <div
        className="void-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={t({ en: 'Manager password', ar: 'كلمة مرور المدير' })}
      >
        <header className="void-dialog-header">
          <h2>{t({ en: 'Manager approval', ar: 'موافقة المدير' })}</h2>
        </header>
        <div className="void-dialog-body">
          {reason && <p style={{ marginTop: 0, fontWeight: 600 }}>{t(reason)}</p>}
          <label className="void-dialog-label" htmlFor="manager-password">
            {t({ en: "Manager's password", ar: 'كلمة مرور المدير' })}
          </label>
          {/* NEVER saved or autofilled by the browser (asked 2026-09-21): it
              is somebody else's password typed on a shared till. A
              type="password" field is what password managers look for — and
              Chrome takes one that disappears after a request as a login to
              offer saving — so where the browser can mask text itself this is
              a plain TEXT field drawn as dots, which they ignore. Elsewhere it
              falls back to a password field that asks not to be filled.
              Either way: no <form>, a name no manager recognises, the
              extensions' own "ignore me" attributes, and the value is wiped
              when the dialog closes. */}
          <input
            id="manager-password"
            ref={inputRef}
            type={MASKABLE_TEXT ? 'text' : 'password'}
            name={FIELD_NAME}
            autoComplete={MASKABLE_TEXT ? 'off' : 'new-password'}
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            inputMode="text"
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
            data-form-type="other"
            className="receipts-input"
            value={password}
            disabled={busy}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submit();
            }}
            style={{
              width: '100%',
              ...(MASKABLE_TEXT ? ({ WebkitTextSecurity: 'disc' } as React.CSSProperties) : {}),
            }}
          />
          {error && (
            <div
              role="alert"
              style={{
                marginTop: 10,
                padding: '10px 12px',
                borderRadius: 8,
                background: '#B91C1C',
                color: '#FFFFFF',
                fontWeight: 600,
              }}
            >
              {error}
            </div>
          )}
        </div>
        <footer className="void-dialog-actions">
          <button
            type="button"
            className="receipts-btn receipts-btn-secondary"
            disabled={busy}
            onClick={() => finish(null)}
          >
            {t({ en: 'Back', ar: 'رجوع' })}
          </button>
          <button
            type="button"
            className="receipts-btn receipts-btn-primary"
            disabled={busy || !password}
            onClick={() => void submit()}
          >
            {busy ? t({ en: 'Checking…', ar: 'جارٍ التحقق…' }) : t({ en: 'Approve', ar: 'موافقة' })}
          </button>
        </footer>
      </div>
    </div>
  );
}
